<template>
  <transition
    name="unpin-slide"
    @after-leave="afterLeave"
  >
    <div v-if="!isLeaving" class="info-card" :class="{ expanded: showFooter }">
      <div class="info-card-content">
        <!-- Header row: icon + title + pin -->
        <div class="billing-header">
          <div class="icon-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
              <path d="M8.44444 19.1675V11.6325L2.11111 7.81V15.345L8.44444 19.1675ZM10.5556 19.1675L16.8889 15.345V7.81L10.5556 11.6325V19.1675ZM8.44444 21.6975L1.05556 17.27C0.721296 17.0683 0.461806 16.8025 0.277083 16.4725C0.0923611 16.1425 0 15.7758 0 15.3725V6.6275C0 6.22417 0.0923611 5.8575 0.277083 5.5275C0.461806 5.1975 0.721296 4.93167 1.05556 4.73L8.44444 0.3025C8.7787 0.100833 9.13056 0 9.5 0C9.86944 0 10.2213 0.100833 10.5556 0.3025L17.9444 4.73C18.2787 4.93167 18.5382 5.1975 18.7229 5.5275C18.9076 5.8575 19 6.22417 19 6.6275V15.3725C19 15.7758 18.9076 16.1425 18.7229 16.4725C18.5382 16.8025 18.2787 17.0683 17.9444 17.27L10.5556 21.6975C10.2213 21.8992 9.86944 22 9.5 22C9.13056 22 8.7787 21.8992 8.44444 21.6975ZM13.7222 7.1775L15.7542 5.9675L9.5 2.2L7.44167 3.4375L13.7222 7.1775ZM9.5 9.735L11.5583 8.4975L5.30417 4.73L3.24583 5.9675L9.5 9.735Z" fill="#25B8C2"/>
            </svg>
          </div>
          <p class="info-text-title">Billing:</p>
          <div class="header-action-icons">
            <div v-if="!isCardPinned" class="action-icon" @click.stop="handlePinCard">
              <img src="@/assets/cardActions/pin.svg" alt="Pin" />
            </div>
            <div v-if="isCardPinned" class="action-icon" @click.stop="startUnpin">
              <img src="@/assets/cardActions/unpin.svg" alt="Unpin" />
            </div>
          </div>
        </div>

        <!-- Content: billing orders list -->
        <div class="billing-content">
          <div v-if="displayLoading" class="loading-state">
            <p class="info-text">Loading billing orders...</p>
          </div>
          <div v-else-if="displayBillingOrders.length > 0" class="billing-orders-list">
            <div
              v-for="order in displayBillingOrders"
              :key="order.sample_id"
              class="billing-order-item"
              @click="handleOrderClick(order)"
            >
              <div class="order-main-info">
                <div class="order-id-row">
                  <p class="order-id-text">Order ID: {{ order.julien_barcode || order.sample_id }}</p>
                </div>
                <div class="order-amount-row">
                  <p class="order-amount">${{ formatAmount(order.order_total) }}</p>
                </div>
                <div class="charge-method-row">
                  <span class="charge-method-tag">{{ formatChargeMethod(order.charge_method) }}</span>
                </div>
                <div class="status-row">
                  <span v-if="order.payment_status" class="status-badge" :class="getPaymentStatusClass(order.payment_status)">
                    {{ order.payment_status }}
                  </span>
                  <span v-if="order.payment_status" class="status-date">({{ formatDate(order.updated_time) }})</span>
                  <span v-if="order.refund_status && order.refund_status !== 'Unrefund'" class="status-badge refund">
                    {{ order.refund_status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p class="info-text">No billing orders found for this patient.</p>
          </div>
        </div>
      </div>
      <div class="card-expand" :class="{ 'justify-end': !showFooter, 'justify-between': showFooter }">
        <FoldableButton v-if="showFooter" customized_class="drag-handle" text="Move">
          <template #prefix>
            <img src="@/assets/cardActions/move.svg" alt="Move" />
          </template>
        </FoldableButton>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useCardActions } from '@/composables/useCardActions'
import { usePatientInfoStore } from '@/store/helpers/my-patients/patient-info'

// Define props
const props = defineProps({
  patient: {
    type: String,
    default: ''
  },
  cardData: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  context: {
    type: String,
    default: 'pegboard'
  },
  patientId: {
    type: [String, Number],
    required: true
  },
  billingOrders: {
    type: Array,
    default: undefined
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

// Define emits
const emit = defineEmits(['pin-card', 'unpin-card', 'order-click'])

const patientInfoStore = usePatientInfoStore()

// Use the card actions composable
const { isCardPinned, handlePinCard, handleUnpinCard } = useCardActions(props, emit)

const isLeaving = ref(false)

// Display billing orders - prioritize props (from parent) over store data
const displayBillingOrders = computed(() => {
  if (props.billingOrders && props.billingOrders.length > 0) {
    return props.billingOrders
  }
  return patientInfoStore.state.currentPatientBillingOrders.value || []
})

const displayLoading = computed(() => {
  return props.isLoading || patientInfoStore.state.billingOrdersLoading.value || false
})

const startUnpin = () => {
  if(props.showFooter){
    isLeaving.value = true
  } else {
    handleUnpinCard()
  }
}

const afterLeave = () => {
  emit('unpin-card', props.cardData)
}

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return 'N/A'
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = months[date.getMonth()]
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}-${day}-${year}`
  } catch (e) {
    return 'N/A'
  }
}

// Format amount with 2 decimal places
const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toFixed(2)
}

// Format charge method for display
const formatChargeMethod = (method) => {
  const methodMap = {
    'customerPay': 'Provider Office Bill',
    'patientPayNow': 'Patient Pay Now',
    'patientPayLater': 'Patient Pay Later'
  }
  return methodMap[method] || method
}

// Get payment status CSS class
const getPaymentStatusClass = (status) => {
  const statusMap = {
    'Paid': 'paid',
    'Unpaid': 'unpaid',
    'Partially Paid': 'partial'
  }
  return statusMap[status] || 'unpaid'
}

// Handle order click - navigate to VA portal patient profile page
const handleOrderClick = (order) => {
  emit('order-click', order)
  const token = sessionStorage.getItem('token')
  const portalUrl = `https://portal.vibrant-wellness.com/#/patient-profile/${props.patientId}/${order.julien_barcode}/${order.sample_id}?jwtToken=${token}`
  window.open(portalUrl, '_blank')
}

</script>

<style scoped>
.unpin-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.unpin-slide-leave-to {
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
}

.info-card {
  background-color: white;
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 25px 0px rgba(74, 74, 74, 0.15);
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.billing-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.billing-header .info-text-title {
  flex: 1;
}

.icon-container {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.info-text-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #666;
  margin: 0;
  line-height: 24px;
}

.header-action-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
}

.action-icon {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  padding: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-icon:hover {
  background-color: #E5F1FB;
}

.billing-content {
  margin-left: 32px;
}

.loading-state,
.empty-state {
  padding: 8px 0;
  text-align: center;
  color: #666666;
  font-style: italic;
  margin-left: -32px;
}

.empty-state {
  color: #999999;
}

.info-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0.25px;
  line-height: 20px;
}

/* Billing Orders List */
.billing-orders-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  width: 100%;
}

/* Scrollbar styling - overlay style */
.billing-orders-list::-webkit-scrollbar {
  width: 0px;
}

.billing-orders-list:hover::-webkit-scrollbar {
  width: 4px;
}

.billing-orders-list::-webkit-scrollbar-track {
  background: transparent;
}

.billing-orders-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.billing-orders-list::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.billing-order-item {
  width: 100%;
  flex-shrink: 0;
  border-radius: 5px;
  background: var(--Cool-White, #F9FBFD);
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.billing-order-item:hover {
  background: #E5F1FB;
}

.order-main-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.order-id-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-id-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #333;
  line-height: 16px;
  letter-spacing: 0.4px;
  margin: 0;
}

.order-amount-row {
  display: flex;
  align-items: center;
}

.order-amount {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 20px;
  margin: 0;
}

.charge-method-row {
  display: flex;
  align-items: center;
}

.charge-method-tag {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.25px;
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  color: #666;
  background: #E8E8E8;
}

.status-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.status-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.25px;
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

.status-badge.paid {
  color: #25B8C2;
  background: #EBFFF8;
}

.status-badge.unpaid {
  color: #F06539;
  background: #F7EECE;
}

.status-badge.partial {
  color: #1E4670;
  background: #E5F1FB;
}

.status-badge.refund {
  color: #D10000;
  background: #F6CCCC;
}

.status-date {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: #666;
  line-height: 14px;
  letter-spacing: 0.1px;
}

/* Hover footer */
.card-expand {
  display: flex;
  align-items: center;
  gap: 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-top-color: transparent;
  pointer-events: none;
  transition: max-height 300ms linear,
              opacity 300ms linear,
              padding-top 300ms linear,
              margin-top 300ms linear,
              border-top-color 300ms linear;
}

.info-card.expanded:hover .card-expand {
  max-height: 80px;
  opacity: 1;
  padding-top: 10px;
  margin-top: 10px;
  border-top-color: var(--DividerGray, #C4C4C4);
  pointer-events: auto;
}

.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 5px;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  text-decoration-line: underline;
}

.expand-action:hover {
  background-color: #E5F1FB;
}

.move-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.4s ease, padding 0.4s ease, gap 0.4s ease;
  gap: 0;
}

.move-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  width: 78px;
}

.move-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.1s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.move-expand:hover span {
  opacity: 1;
}

.move-expand img {
  flex-shrink: 0;
}
</style>
